import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ArchiveDocument, Order, PreferredResolutionType, ProblemType, RegisterClaim, DocumentType } from "@/types";
import { Grid, Chip } from "@mantine/core";
import { useForm, Controller, SubmitHandler } from "react-hook-form";
import { ClaimsFormInput, ClaimsSchema } from "./validationSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { postRegisterClaim } from "@/services/Claims";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { fileToBase64Raw, getFileTypeFromFile } from "@/utils/FileUtils";
import { MIME_TYPES } from "@mantine/dropzone";
import Textarea from "@/components/atoms/Textarea";
import Select from "@/components/atoms/Select";
import CTA from "@/components/atoms/CTA";
import Text from "@/components/atoms/Text";
import Stack from "@/components/atoms/Stack";
import Accordion from "@/components/organisms/AccordionDrawer";
import Dropzone from "@/components/molecules/Dropzone";
import { useAuth } from "@/contexts/AuthContext";
import dayjs from "dayjs";
import Group from "@/components/atoms/Group";

import styles from "./styles.module.css";
import ClaimPreferredResolutionEnum from "@/constants/enums/ClaimPreferredResolutionEnum";

interface NewClaimProps {
  order: Order;
}

const NewClaim = ({ order }: NewClaimProps) => {
  const [isCreatingClaim, setIsCreatingClaim] = useState(false);
  return (
    <>
      {!isCreatingClaim && <CreateNewClaimCTA onClick={() => setIsCreatingClaim(true)} />}
      {isCreatingClaim && <NewClaimForm order={order} setIsCreatingClaim={setIsCreatingClaim} />}
    </>
  );
};
export default NewClaim;

const CreateNewClaimCTA = ({ onClick }: { onClick: () => void }) => {
  const { t } = useTranslation();
  return (
    <Group justify="center" className={styles.newClaimButton}>
      <CTA size="lg" intent="secondary" data-testid="new-claim-btn" onClick={onClick}>
        {t("claims:newClaim")}
      </CTA>
    </Group>
  );
};

interface NewClaimsFormProps {
  order: Order;
  setIsCreatingClaim: (isCreating: boolean) => void;
}

const NewClaimForm = ({ order, setIsCreatingClaim }: NewClaimsFormProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const { currentCompany, user } = useAuth();

  const {
    handleSubmit,
    control,
    reset,
    watch,
    formState: { errors },
  } = useForm<ClaimsFormInput>({
    resolver: zodResolver(ClaimsSchema),
  });

  const { mutate: registerClaim, isPending: isPendingRegisterClaim } = useMutation({
    mutationFn: async (claim: RegisterClaim) => {
      await postRegisterClaim(claim);
    },
    onSuccess: async () => {
      reset();
      setIsCreatingClaim(false);
      await queryClient.invalidateQueries({
        queryKey: ["getClaimsByOrderId"],
      });
    },
    onError: (error) => {
      console.error("Error creating claim:", error);
    },
  });

  const onSubmit: SubmitHandler<ClaimsFormInput> = async (data) => {
    if (!order.transactions) return;

    const attachments = watch("attachments") || [];
    let base64Images: ArchiveDocument[] = [];
    if (attachments.length > 0) {
      base64Images = await Promise.all(
        attachments.map(async (image) => {
          const base64File = await fileToBase64Raw(image);
          return {
            document: base64File,
            companyIds: [currentCompany?.companyId] as number[],
            languageId: user?.language.id as number,
            fileType: getFileTypeFromFile(image),
            documentType: "IMAGE" as DocumentType,
            documentTitle: image.name,
          };
        })
      );
    }

    const claimsData: RegisterClaim = {
      orderId: order.id,
      transactionId: order.transactions[0].id,
      problemTypes: data.problemTypes,
      preferredResolution: data.preferredResolution as PreferredResolutionType,
      customerNote: data.customerNote,
      attachments: base64Images,
      createdByCompanyId: currentCompany?.companyId as number,
      createdBy: user?.id as number,
      created: dayjs().format(),
    };
    registerClaim(claimsData);
  };

  const PreferredResolution: {
    value: PreferredResolutionType;
    label: string;
  }[] = [
    {
      value: ClaimPreferredResolutionEnum.REFUND_FULL,
      label: t("enumerables:preferredSolutions.REFUND_FULL"),
    },
    {
      value: ClaimPreferredResolutionEnum.REFUND_PARTIAL,
      label: t("enumerables:preferredSolutions.REFUND_PARTIAL"),
    },
    {
      value: ClaimPreferredResolutionEnum.REPLACEMENT,
      label: t("enumerables:preferredSolutions.REPLACEMENT"),
    },
    {
      value: ClaimPreferredResolutionEnum.RETURN_ITEM,
      label: t("enumerables:preferredSolutions.RETURN_ITEM"),
    },
  ];

  return (
    <Accordion title={t("claims:newClaim")} initiallyOpen>
      <form>
        <Grid className={styles.grid}>
          <Grid.Col span={12}>
            <Stack>
              <Text size="lg" color="tertiary">
                {t("claims:newClaimHeader")}
              </Text>
              <Text size="sm" color="secondary">
                {t("claims:newClaimDescription")}
              </Text>
            </Stack>
          </Grid.Col>

          <Grid.Col span={12}>
            <Stack>
              <div className={styles.chipWrapper}>
                <Controller
                  control={control}
                  name="problemTypes"
                  render={({ field: { onChange, value } }) => (
                    <Chip.Group multiple value={value} onChange={onChange}>
                      {ProblemTypes.map((problemType) => (
                        <Chip
                          key={problemType}
                          size="md"
                          value={problemType}
                          data-testid={`problem-type-chip-${problemType}`}
                          classNames={{
                            label: styles.chipLabel,
                            checkIcon: styles.chipIcon,
                            iconWrapper: styles.iconWrapper,
                          }}
                          styles={{
                            root: {
                              "--chip-bg": "var(--color-text-primary)",
                              "--chip-hover": "var(--color-text-primary)",
                            },
                          }}
                        >
                          <Text size="sm">{t(`enumerables:problemTypes.${problemType}`)}</Text>
                        </Chip>
                      ))}
                    </Chip.Group>
                  )}
                />
              </div>
              {errors.problemTypes && (
                <Text size="xs" color="error">
                  {t("validation:" + errors.problemTypes?.message)}
                </Text>
              )}
            </Stack>
          </Grid.Col>

          <Grid.Col span={7}>
            <Controller
              control={control}
              name="customerNote"
              render={({ field: { onChange, value } }) => (
                <Textarea
                  data-testid="claim-customer-note"
                  label={t("claims:provideDetails")}
                  value={value}
                  onChange={(e) => onChange(e.target.value)}
                  error={errors.customerNote ? t("validation:" + errors.customerNote?.message) : null}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={7}>
            <Controller
              control={control}
              name="preferredResolution"
              render={({ field: { onChange, value } }) => (
                <Select
                  data-testid="claim-preferred-resolution"
                  label={t("claims:preferredSolution")}
                  data={PreferredResolution}
                  value={value}
                  onChange={(e) => onChange(e as PreferredResolutionType)}
                  error={errors.preferredResolution ? t("validation:" + errors.preferredResolution?.message) : null}
                />
              )}
            />
          </Grid.Col>
          <Grid.Col span={7}>
            <Controller
              control={control}
              name="attachments"
              render={({ field: { onChange, value = [] } }) => (
                <Dropzone
                  origin="local"
                  type={"button"}
                  files={value}
                  setFiles={onChange}
                  buttonPosition="left"
                  buttonLabel={t("claims:uploadImages")}
                  accept={[MIME_TYPES.jpeg, MIME_TYPES.png]}
                />
              )}
            />
            {errors.attachments && (
              <Text size="xs" color="error">
                {t("validation:" + errors.attachments?.message)}
              </Text>
            )}
          </Grid.Col>

          <Grid.Col span={12}>
            <Group justify="end">
              <CTA intent="secondary" loading={isPendingRegisterClaim} onClick={() => setIsCreatingClaim(false)}>
                {t("actions:cancel")}
              </CTA>
              <CTA
                type="submit"
                data-testid="register-claim-btn"
                loading={isPendingRegisterClaim}
                onClick={handleSubmit(onSubmit)}
              >
                {t("claims:registerClaim")}
              </CTA>
            </Group>
          </Grid.Col>
        </Grid>
      </form>
    </Accordion>
  );
};

const ProblemTypes: ProblemType[] = [
  "PRODUCT_DESCRIPTION",
  "DELIVERY_TIME",
  "PRODUCT_QUALITY",
  "DELIVERY_LOCATION",
  "COMMUNICATION",
  "DOCUMENTATION",
  "PACKAGING",
  "OTHER",
];
